import { FDRStatus, FieldModel } from '@cibo/core'
import { FieldColumnProps } from '@cibo/landmanager'
import { LineLimitTypography, ResponsiveDialog } from '@cibo/ui'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Popover,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { FC, ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ProgramIcon, useProgramContent } from '../..'
import { AllProgramReasons } from '../AllProgramReasons'

export interface FieldOpportunitiesColumnProps extends FieldColumnProps {
  onClickNeedsInfo?: (field: FieldModel) => void
  onClickOpportunity?: (props: { field: FieldModel; programId: string }) => void
  programId?: string
}

export const ProgramOpportunityTeaser = ({
  field,
  programId,
  size = 'sm',
  t,
  onClickOpportunity,
  display,
}: { field: FieldModel; programId: string; size?: 'sm' | 'lg'; display?: 'grid' | 'list' } & Omit<
  FieldOpportunitiesColumnProps,
  'field'
>) => {
  const program = useProgramContent(programId)
  const status = field.fdrStatus.find(p => p.programId === programId)
  if ((program.isLoading || program.isFetching) && !program.isError) {
    return display === 'grid' ? (
      <Grid item md={6}>
        <Skeleton width={'100%'} height={60} />
      </Grid>
    ) : (
      <Skeleton width={125} height={60} />
    )
  }
  if (
    !program.data ||
    !(
      status?.status.includes(FDRStatus.eligibleForEnrollment) ||
      status?.status.includes(FDRStatus.futureEnrollment)
    )
  )
    return null

  // @todo support other tokens!
  const dollars = field.programTokenPotential({ programId, token: 'usd' })
  const body = (
    <Stack key={programId} direction="row" spacing={1}>
      <ProgramIcon program={program.data} size={size === 'sm' ? 20 : 40} />
      <Stack alignItems="flex-start">
        <LineLimitTypography
          color="text.secondary"
          lineLimit={1}
          textAlign="left"
          variant={size === 'sm' ? 'caption' : 'body2'}
        >
          {program.data.content.fields.title}
        </LineLimitTypography>
        {dollars > 0 && (
          <Stack>
            <LineLimitTypography
              lineLimit={1}
              textAlign="left"
              variant={size === 'sm' ? 'body2' : 'body1'}
            >
              {t('upToDollarsAmount', { dollars })}
            </LineLimitTypography>
          </Stack>
        )}
      </Stack>
    </Stack>
  )

  const opportunityItem = !!onClickOpportunity ? (
    <Button
      onClick={e => {
        e.stopPropagation()
        onClickOpportunity({ programId, field })
      }}
    >
      {body}
    </Button>
  ) : (
    <>{body}</>
  )
  return display === 'grid' ? (
    <Grid item md={6} key={programId}>
      {opportunityItem}
    </Grid>
  ) : (
    <Box key={programId}>{opportunityItem}</Box>
  )
}

export const ProgramOpportunitiesDisplay: FC<
  { field: FieldModel; display: 'grid' | 'list'; isFetched?: boolean } & Omit<
    FieldOpportunitiesColumnProps,
    'field' | 't'
  >
> = ({ field, onClickOpportunity, children, display, programId }) => {
  const { t } = useTranslation('@cibo/programs/FieldColumns')
  const [reasonsOpen, setReasonsOpen] = useState(false)
  const {
    palette: { secondary },
  } = useTheme()

  if (field.isPartnerField) {
    return <></>
  }

  if (!field.footprintResolved) {
    return (
      <Stack direction="row" alignItems="center">
        <Stack alignItems="center" justifyContent="center" width={36}>
          <CircularProgress size={16} />
        </Stack>
        <Typography variant="caption">{t('findingOpportunities')}</Typography>
      </Stack>
    )
  }

  /**
   * Should we look for the existence of rewards?  I _think_ so, but availableForWorkflow
   * ought to match as well.
   */
  const rewardablePrograms = field.fdrStatus.filter(
    ({ status }) =>
      status.includes(FDRStatus.eligibleForEnrollment) ||
      status.includes(FDRStatus.futureEnrollment)
  )
  const ineligiblePrograms = field.fdrStatus.filter(
    ({ status }) =>
      status.includes(FDRStatus.ruleIneligible) &&
      !status.includes(FDRStatus.historicalProgram) &&
      !status.includes(FDRStatus.futureProgram) &&
      !status.includes(FDRStatus.enrolling)
  )
  const enrollingPrograms = field.fdrStatus.filter(
    ({ status }) => status.includes(FDRStatus.enrolling) || status.includes(FDRStatus.enrolled)
  )

  const reasonsDialog = (
    <ResponsiveDialog
      open={reasonsOpen}
      onClose={() => setReasonsOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{field.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body2">
            {enrollingPrograms.length > 0
              ? t('notFurtherQualifiedDescription', {
                  count: rewardablePrograms.length,
                })
              : t('notQualifiedDescription', { count: rewardablePrograms.length })}
          </Typography>
          <Stack spacing={6}>
            <AllProgramReasons field={field} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => setReasonsOpen(false)}>
          {t('close')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  )

  const reasonsLink =
    ineligiblePrograms.length > 0 ? (
      <>
        <Link
          color="secondary"
          variant="caption"
          data-testid="ineligible-programs"
          onClick={(e: any) => {
            e.preventDefault()
            e.stopPropagation()
            setReasonsOpen(true)
          }}
          sx={{ cursor: 'pointer' }}
          underline="none"
        >
          {t('ineligiblePrograms', { count: ineligiblePrograms.length })}
        </Link>
        {reasonsDialog}
      </>
    ) : null

  //no opportunities
  if (
    !rewardablePrograms.length &&
    !field.programStatuses?.some(
      s =>
        !!s.participating ||
        !!s.workflow ||
        !!s.benchmarks.find(b => b.benchmark === 'profile' && b.status === 'incomplete')
    )
  ) {
    return (
      <>
        <Stack spacing={0.5}>
          {children}
          <Box>
            <Button
              color="secondary"
              size="small"
              onClick={e => {
                e.stopPropagation()
                setReasonsOpen(true)
              }}
              sx={{ textAlign: 'left' }}
            >
              <Stack direction="row" alignItems="center" spacing={1} overflow="hidden">
                <LineLimitTypography lineLimit={1} variant="caption">
                  {t('noOpportunities')} - <span style={{ color: secondary.main }}>{t('why')}</span>
                </LineLimitTypography>
              </Stack>
            </Button>
          </Box>
        </Stack>
        {reasonsDialog}
      </>
    )
  }

  const rewardableProgramTiles = rewardablePrograms
    .map(({ programId }) => (
      <ProgramOpportunityTeaser
        key={programId}
        field={field}
        programId={programId}
        t={t}
        onClickOpportunity={onClickOpportunity}
        display={display}
      />
    ))
    .filter(a => a !== null)

  if (rewardableProgramTiles.length > 0) {
    return display === 'grid' ? (
      <Box>
        {children}
        <Grid container spacing={2}>
          {rewardableProgramTiles}
        </Grid>
      </Box>
    ) : (
      <Stack spacing={0.5} mx={0.5}>
        {children}
        {rewardablePrograms.length > 2 ? (
          <>
            {rewardableProgramTiles[0]}
            <AndCountMore opportunities={rewardableProgramTiles} />
          </>
        ) : (
          rewardableProgramTiles
        )}
        {reasonsLink}
      </Stack>
    )
  }
  return reasonsLink
}

const AndCountMore = ({ opportunities }: { opportunities: ReactElement[] }) => {
  const { t } = useTranslation('@cibo/programs/FieldColumns')
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const handleClose = () => {
    setAnchorEl(null)
  }
  const open = Boolean(anchorEl)
  const id = open ? 'additional-programs' : undefined

  return (
    <>
      <Button
        data-testid="additional-programs"
        aria-haspopup="true"
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          setAnchorEl(e.currentTarget)
        }}
        endIcon={<ArrowDropDown />}
      >
        {t('moreThanTwo', { count: opportunities.length - 1 })}
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Stack spacing={0.5} m={0.5}>
          {opportunities.slice(1)}
        </Stack>
      </Popover>
    </>
  )
}

export const fieldOpportunitiesColumn = ({
  onClickNeedsInfo,
  onClickOpportunity,
  programId,
  t,
  ...columnProps
}: FieldOpportunitiesColumnProps) =>
  ({
    field: 'opportunities',
    headerName: t('opportunities'),
    // API doesn't support sorting at present
    sortable: false,
    filterable: false,
    minWidth: 200,
    flex: 1,
    renderCell: (params: GridRenderCellParams<any, FieldModel>) =>
      !params.row.fdrGlobalStatus.includes(FDRStatus.needsInfo) ? (
        <Box overflow="hidden">
          <ProgramOpportunitiesDisplay
            display="list"
            field={params.row}
            onClickNeedsInfo={onClickNeedsInfo}
            onClickOpportunity={onClickOpportunity}
            programId={programId}
          />
        </Box>
      ) : null,
    ...columnProps,
  } as GridColDef<FieldModel>)
